import type { DataLayerEvent } from './data-layer';

type QueuedDataLayerEvent = {
    event: DataLayerEvent;
    resolve: () => void;
    reject: (reason?: unknown) => void;
};

let hasInitialLogState = false;
let hasStartedGtm = false;
let pendingPageKey: string | null = null;
let committedPageKey: string | null = null;
let queuedEvents: QueuedDataLayerEvent[] = [];
/** `view_item_list` keys already pushed for the current page. */
let trackedItemListViews = new Set<string>();

/**
 * Marks the current route as waiting for its `page_view`. Events other than
 * `log_state` / `page_view` / `login` / `sign_up` stay queued until that view is committed.
 */
export function markPendingPageView(pageKey: string): void {
    if (committedPageKey === pageKey) {
        return;
    }
    if (pendingPageKey !== pageKey) {
        trackedItemListViews = new Set();
    }
    pendingPageKey = pageKey;
}

/**
 * Returns `false` when the same list was already viewed on the current page. Lives at module
 * level so a remounted listing (StrictMode, hydration recovery) cannot push it twice.
 */
export function claimItemListView(listKey: string): boolean {
    const key = `${pendingPageKey ?? ''}|${listKey}`;
    if (trackedItemListViews.has(key)) {
        return false;
    }
    trackedItemListViews.add(key);
    return true;
}

export function markGoogleTagManagerStarted(): void {
    hasStartedGtm = true;
}

export function hasGoogleTagManagerStarted(): boolean {
    return hasStartedGtm;
}

export function shouldQueueDataLayerEvent(event: DataLayerEvent): boolean {
    if (event.event === 'log_state') {
        return false;
    }
    if (!hasInitialLogState) {
        return true;
    }
    if (event.event === 'page_view' || event.event === 'login' || event.event === 'sign_up') {
        return false;
    }
    return pendingPageKey !== null && committedPageKey !== pendingPageKey;
}

export function queueDataLayerEvent(event: DataLayerEvent): Promise<void> {
    return new Promise((resolve, reject) => {
        queuedEvents.push({ event, resolve, reject });
    });
}

export function noteDataLayerEventCommitted(event: DataLayerEvent): void {
    if (event.event === 'log_state') {
        hasInitialLogState = true;
    }
    if (event.event === 'page_view') {
        committedPageKey = pendingPageKey;
    }
}

export function takeReadyQueuedDataLayerEvents(): QueuedDataLayerEvent[] {
    const ready: QueuedDataLayerEvent[] = [];
    const held: QueuedDataLayerEvent[] = [];

    for (const item of queuedEvents) {
        if (shouldQueueDataLayerEvent(item.event)) {
            held.push(item);
        } else {
            ready.push(item);
        }
    }

    queuedEvents = held;
    return ready;
}

export function resetDataLayerBootstrapForTests(): void {
    hasInitialLogState = false;
    hasStartedGtm = false;
    pendingPageKey = null;
    committedPageKey = null;
    queuedEvents = [];
    trackedItemListViews = new Set();
}

/** Unlocks the queue so unit tests can push events without a full bootstrap. */
export function allowDataLayerEventsForTests(): void {
    hasInitialLogState = true;
    pendingPageKey = 'test';
    committedPageKey = 'test';
}
